import React, { useEffect, useRef } from 'react';
import { Popover } from 'antd';

interface EditableMetricInputProps {
    value: string;
    warningMessage: string;
    isEditing: boolean;
    onChange: (value: string) => void;
    onSubmit: () => void;
    onCancel: () => void;
    onValidate: (value: string) => boolean;
}

const EditableMetricInput: React.FC<EditableMetricInputProps> = ({
    value,
    warningMessage,
    isEditing,
    onChange,
    onSubmit,
    onCancel,
    onValidate,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.select();
        }
    }, [isEditing]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const allowedKeys = ['Backspace', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'];
        const isAllowed =
            /[0-9.\-]/.test(e.key) ||
            allowedKeys.includes(e.key) ||
            (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()));

        if (!isAllowed) {
            e.preventDefault();
        }

        if (e.key === 'Enter') {
            if (onValidate(value)) {
                onSubmit();
            }
            onCancel(); // Close editing
        }

        if (e.key === 'Escape') {
            onCancel();
        }
    };

    const handleBlur = () => {
        if (onValidate(value)) {
            onSubmit();
        }
        onCancel();
    };

    return (
        <Popover
            content={<span className="text-redAlt text-xs">{warningMessage}</span>}
            open={!!warningMessage}
            placement="bottomLeft"
        >
            <input
                ref={inputRef}
                type="text"
                className="border-none w-full px-1 h-full"
                autoFocus
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
            />
        </Popover>
    );
};

export default EditableMetricInput;
